import { useState } from 'react';
import { Text, Badge, makeStyles, tokens } from '@fluentui/react-components';
import type { ModuleCropHistoryEntry } from '../../api/terrain.api';
import { useT } from '../../i18n';
import { useLocaleSettings } from '../../hooks/useLocaleSettings';

/**
 * Historial de una zona o subzona: vigencias de cultivo con filtros por
 * fechas, actividad (motivo del cambio) y estado (vigente o cerrado).
 *
 * Un registro cuenta como "de la zona" en el rango si su vigencia se solapa
 * con él: empieza antes del fin del rango y termina después del inicio.
 */

export type ModuleHistoryStatusFilter = 'ALL' | 'CURRENT' | 'CLOSED';
export type ModuleHistoryReasonFilter = 'ALL' | ModuleCropHistoryEntry['reason'];

export interface ModuleHistoryFilters {
  from: string;
  to: string;
  reason: ModuleHistoryReasonFilter;
  status: ModuleHistoryStatusFilter;
}

export const EMPTY_MODULE_HISTORY_FILTERS: ModuleHistoryFilters = {
  from: '',
  to: '',
  reason: 'ALL',
  status: 'ALL',
};

export function filterModuleHistory(
  entries: ModuleCropHistoryEntry[],
  filters: ModuleHistoryFilters,
): ModuleCropHistoryEntry[] {
  return entries.filter(entry => {
    if (filters.reason !== 'ALL' && entry.reason !== filters.reason) return false;
    if (filters.status === 'CURRENT' && entry.endDate !== null) return false;
    if (filters.status === 'CLOSED' && entry.endDate === null) return false;
    if (filters.to && entry.startDate > filters.to) return false;
    if (filters.from && entry.endDate !== null && entry.endDate < filters.from) return false;
    return true;
  });
}

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', alignItems: 'flex-end' },
  field: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  input: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  item: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: tokens.spacingVerticalS,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
});

interface ModuleHistoryPanelProps {
  entries: ModuleCropHistoryEntry[];
  cropNames: Record<string, string>;
}

export function ModuleHistoryPanel({ entries, cropNames }: ModuleHistoryPanelProps) {
  const s = useStyles();
  const t = useT();
  const { formatDate } = useLocaleSettings();
  const [filters, setFilters] = useState<ModuleHistoryFilters>(EMPTY_MODULE_HISTORY_FILTERS);

  const visible = filterModuleHistory(entries, filters);
  const labels = t.investorModules;

  const reasonLabel = (reason: ModuleCropHistoryEntry['reason']) => {
    if (reason === 'CREATE_MODULE') return labels.historyReasonCreate;
    if (reason === 'UPDATE_MODULE') return labels.historyReasonUpdate;
    return labels.historyReasonBackfill;
  };

  return (
    <div className={s.root}>
      <div className={s.filters}>
        <div className={s.field}>
          <label htmlFor="history-from"><Text size={200}>{labels.historyFrom}</Text></label>
          <input
            id="history-from"
            type="date"
            className={s.input}
            value={filters.from}
            onChange={e => setFilters({ ...filters, from: e.target.value })}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="history-to"><Text size={200}>{labels.historyTo}</Text></label>
          <input
            id="history-to"
            type="date"
            className={s.input}
            value={filters.to}
            onChange={e => setFilters({ ...filters, to: e.target.value })}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="history-reason"><Text size={200}>{labels.historyActivity}</Text></label>
          <select
            id="history-reason"
            className={s.input}
            value={filters.reason}
            onChange={e => setFilters({ ...filters, reason: e.target.value as ModuleHistoryReasonFilter })}
          >
            <option value="ALL">{labels.historyAll}</option>
            <option value="CREATE_MODULE">{labels.historyReasonCreate}</option>
            <option value="UPDATE_MODULE">{labels.historyReasonUpdate}</option>
            <option value="BACKFILL">{labels.historyReasonBackfill}</option>
          </select>
        </div>
        <div className={s.field}>
          <label htmlFor="history-status"><Text size={200}>{labels.historyStatus}</Text></label>
          <select
            id="history-status"
            className={s.input}
            value={filters.status}
            onChange={e => setFilters({ ...filters, status: e.target.value as ModuleHistoryStatusFilter })}
          >
            <option value="ALL">{labels.historyAll}</option>
            <option value="CURRENT">{labels.historyCurrent}</option>
            <option value="CLOSED">{labels.historyClosed}</option>
          </select>
        </div>
      </div>

      {visible.length === 0 ? (
        <Text size={200}>{labels.historyEmpty}</Text>
      ) : (
        <ul className={s.list} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {visible.map(entry => (
            <li key={`${entry.startDate}-${entry.cropId}`} className={s.item}>
              <Text weight="semibold">{cropNames[entry.cropId] ?? entry.cropId}</Text>
              <Badge appearance="tint" size="small">{reasonLabel(entry.reason)}</Badge>
              <Badge appearance="outline" size="small">
                {entry.endDate === null ? labels.historyCurrent : labels.historyClosed}
              </Badge>
              <Text size={200}>
                {labels.historySince} {formatDate(entry.startDate)}
                {' · '}
                {entry.endDate === null
                  ? labels.historyCurrent
                  : `${labels.historyUntil} ${formatDate(entry.endDate)}`}
              </Text>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
